import styled from 'styled-components';
import { ellipsis, rgba, size } from 'polished';
import SimpleBar from 'simplebar-react';
import { typeStyle } from '../../../styles/typeStyle';
import { Paragraph } from '../../atoms/Paragraph/Paragraph';
import { zIndex } from '../../../styles/zIndex';
import { pseudo } from '../../../styles/helpers/pseudo';
import { FontWeight, TransitionDuration } from '../../../styles/variables';
import { AbstractButton } from '../../atoms/AbstractButton/AbstractButton';
import { Icon } from '../../atoms/Icon/Icon';
import { Ease } from '../../../styles/easing';
import { hover } from '../../../styles/helpers/hover';
import { MediaQuery } from '../../../styles/mediaQuery';
import { respondTo } from '../../../styles/helpers/respondTo';

export const StyledLocationSelector = styled.div<{ $isOpen: boolean }>`
  padding-top: 0.9375rem;

  ${({ theme, $isOpen }) =>
    $isOpen &&
    `
    position: fixed;
    top: 0;
    left: 0;
    height: 100%;
    max-height: 100vh;
    width: 100vw;
    padding: 4.9375rem 1.25rem 0;
    z-index: ${zIndex('overlay')};
    background-color: ${theme.color.white};

    &::before {
      ${pseudo()};
      top: 0;
      left: 0;
      height: 3.5rem;
      width: 100vw;
      background-color: ${theme.color.blue};
    }
  `}

  @media ${respondTo(MediaQuery.Large)} {
    position: relative;
    padding: 0.9375rem 0 0;
    height: 6.3125rem;

    > div {
      border: 2px solid transparent;
    }

    &::before {
      content: none;
    }

    ${({ $isOpen, theme }) =>
      $isOpen &&
      `
      width: 100%;

      > div {
        position: absolute;
        top: 2.625rem;
        left: 0.0625rem;
        width: 100%;
        border-color: ${theme.color.lighterGrey};
        background-color: ${theme.color.white};
        box-shadow: 0 0.75rem 1rem rgba(0, 0, 0, 0.05);
      }
    `}
  }
`;

export const StyledInputWrapper = styled.div<{ $isOpen: boolean; $isFocussed: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  height: 3.75rem;
  padding: 0.25rem 3.125rem 0.25rem 3.25rem;
  border: 1px solid ${({ theme }) => theme.color.lighterGrey};
  background-color: ${({ theme }) => theme.color.white};
  cursor: pointer;

  ${({ $isOpen }) =>
    $isOpen &&
    `
    margin 0 -1.25rem;
    height: 3.5rem;
    border-right: 0;
    border-left: 0;
  `}

  &::after {
    ${pseudo()};
    ${size('calc(100% + 4px)')};
    top: -2px;
    left: -2px;
    opacity: 0;
    transition: opacity ${TransitionDuration.Normal} ${Ease.EaseOut};
    border: 2px solid ${({ theme }) => theme.color.lighterGrey};
    pointer-events: none;
  }

  ${({ $isFocussed, theme }) =>
    $isFocussed &&
    `
    &::after {
      width: calc(100% + 2px);
      height: calc(100% + 2px);
      top: -1px;
      left: -1px;
      opacity: 1;
      border: 1px solid ${theme.color.blue};
      box-shadow: 0px 0px 4px ${theme.color.blue};
    }
  `}

  ${hover(`
    &::after {
      opacity: 1;
    }
  `)}

  @media ${respondTo(MediaQuery.Large)} {
    margin: 0;
    padding-left: 3.4375rem;

    ${({ $isOpen }) =>
      $isOpen &&
      `
      border-top: 0;
  `}
  }
`;

export const StyledOverlayLabel = styled(Paragraph)`
  position: absolute;
  top: 1.375rem;
  left: 1.25rem;
  font-weight: ${FontWeight.Bold};
  color: ${({ theme }) => theme.color.white};
`;

export const StyledCloseButton = styled(AbstractButton)`
  ${size('0.875rem')};
  position: absolute;
  top: 1.3125rem;
  right: 1.75rem;
  color: ${({ theme }) => theme.color.white};
`;

export const StyledLabel = styled.label`
  ${typeStyle.normal};
  height: 1rem;
  display: block;
  font-weight: ${FontWeight.Bold};
  margin-bottom: 0.5rem;
`;

export const StyledPlaceholder = styled(AbstractButton)`
  ${typeStyle.large};
  ${ellipsis()};
`;

export const StyledAirplaneIcon = styled(Icon.Airplane)<{ $isDeparture: boolean }>`
  ${size('1.125rem')};
  position: absolute;
  top: 50%;
  left: 1.125rem;
  transform: translateY(-50%) rotate(-135deg);
  color: ${({ theme }) => rgba(theme.color.black, 0.8)};

  ${({ $isDeparture }) =>
    $isDeparture &&
    `
      transform: translateY(-50%) rotate(-45deg);
  `}

  @media ${respondTo(MediaQuery.Large)} {
    left: 1.25rem;
  }
`;

export const StyledChevron = styled(AbstractButton)`
  ${size('1.375rem')};
  position: absolute;
  top: 50%;
  right: 0.9375rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.color.blue};
  transform: translateY(-50%);
  pointer-events: none;

  svg {
    ${size('0.875rem')};
  }

  &:focus {
    border: 1px solid ${({ theme }) => theme.color.blue};
    box-shadow: 0px 0px 4px ${({ theme }) => theme.color.blue};
  }
`;

export const StyledInput = styled.input`
  ${typeStyle.large};
  display: flex;
  align-items: center;
  height: 100%;
  width: 100%;
`;

export const StyledClearButton = styled(AbstractButton)`
  ${size('1.375rem')};
  cursor: pointer;
  position: absolute;
  top: 50%;
  right: 0.9375rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.color.darkerBlue};
  transform: translateY(-50%);
  z-index: ${zIndex('default')};

  svg {
    ${size('0.75rem')};
  }

  &:focus {
    border: 1px solid ${({ theme }) => theme.color.blue};
    box-shadow: 0px 0px 4px ${({ theme }) => theme.color.blue};
  }
`;

export const StyledResultWrapper = styled(SimpleBar)`
  position: absolute !important;
  left: 1.25rem;
  max-height: calc(100% - 9.9375rem);
  width: calc(100% - 1.5625rem);

  .simplebar-content-wrapper {
    mask-image: linear-gradient(to bottom, transparent 0%, black 5%, black 94%, transparent 100%);
  }

  @media ${respondTo(MediaQuery.Large)} {
    position: relative !important;
    top: 0;
    left: 0;
    max-height: 50vh;
    width: calc(100% - 0.3125rem);
  }
`;

export const StyledResultList = styled.ul<{ $additionalSpace?: boolean }>`
  position: relative;
  padding: 0 0 1.25rem 2.25rem;

  @media ${respondTo(MediaQuery.Large)} {
    padding: 0 0 1.25rem 3.4375rem;
  }

  ${({ $additionalSpace }) =>
    $additionalSpace &&
    `
    padding-top: 1.25rem;

    @media ${respondTo(MediaQuery.Large)} {
      padding-top: 1.25rem;
    }
  `}
`;

export const StyledDivider = styled.span`
  ${typeStyle.large};
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0.375rem 0 1.25rem;
  margin-left: -1.875rem;
  color: ${({ theme }) => theme.color.grey};
  white-space: nowrap;

  &::after {
    ${pseudo('block', 'static')};
    ${size('1px', '100%')};
    margin-left: 1.3125rem;
    background-color: ${({ theme }) => theme.color.lighterGrey};
  }

  @media ${respondTo(MediaQuery.Large)} {
    ${typeStyle.normal};
  }
`;

export const StyledListItem = styled(AbstractButton)`
  ${typeStyle.large};
  display: block;
  cursor: pointer;
  margin-bottom: 0.875rem;
  color: ${({ theme }) => theme.color.black};
  transition: transform ${TransitionDuration.Normal} ${Ease.EaseOut};
  text-align: left;

  &.location-is-disabled {
    cursor: not-allowed;
    color: ${({ theme }) => theme.color.grey};
  }

  span {
    display: block;
    margin-top: 0.125rem;
    color: ${({ theme }) => theme.color.grey};
  }

  @media ${respondTo(MediaQuery.Large)} {
    margin-bottom: 1rem;
  }

  &:not(:disabled) {
    ${hover(`
    transform: translateX(0.25rem);
  `)}

    &:focus {
      transform: translateX(0.25rem);
    }
  }
`;
